"use client";

import React, { useMemo } from 'react';
import Sentry from '@/lib/sentry-safe';
import useSWR from 'swr';
import Link from 'next/link';
import { BlogSoundEmbed, BlogSoundEmbedSkeleton } from './blog-sound-embed';
import { type Sound } from '@/lib/supabase';
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count';

interface BlogArticleContentProps {
  /** Pre-rendered HTML content from server */
  htmlContent: string;
  /** DB category name(s), comma-separated, to embed; '' embeds the most downloaded sounds library-wide; omitted embeds none */
  soundCategory?: string;
  /** How many sounds to display (default: 10) */
  soundLimit?: number;
  /** Maps H3 heading text to sound IDs for inline embedding */
  inlineSounds?: Record<string, string>;
  /** Sound IDs listed after the article, in order */
  referenceSoundIds?: string[];
  /** Marketing count token shared with the article shell */
  soundCountLabel?: string;
}

const BLOG_SOUND_SKELETON_ROWS = ['initial', 'middle', 'final'] as const;

/** GET /api/sounds rejects more than 20 ids in one request. */
const MAX_IDS_PER_REQUEST = 20;

function getSegmentKey(segment: { html: string; soundId?: string }): string {
  return segment.soundId ?? segment.html;
}

/**
 * Parse HTML content and split at H3 headings that match inlineSounds keys.
 * Returns segments with their associated sound IDs.
 */
function parseContentWithSounds(
  htmlContent: string,
  inlineSounds: Record<string, string>
): Array<{ html: string; soundId?: string }> {
  // Find all H3 tags and their positions
  const h3Regex = /<h3[^>]*id="([^"]*)"[^>]*>([^<]*)<\/h3>/g;
  const segments: Array<{ html: string; soundId?: string }> = [];

  let lastIndex = 0;
  let match;

  while ((match = h3Regex.exec(htmlContent)) !== null) {
    const fullMatch = match[0];
    const headingText = match[2].trim();
    const matchIndex = match.index;

    // Check if this heading matches any key in inlineSounds
    const matchingKey = headingText in inlineSounds ? headingText : undefined;

    if (matchingKey) {
      // Find the end of the section (next H2/H3 or end of content)
      const nextHeadingRegex = /<h[23][^>]*>/g;
      nextHeadingRegex.lastIndex = matchIndex + fullMatch.length;
      const nextHeading = nextHeadingRegex.exec(htmlContent);

      const sectionEnd = nextHeading ? nextHeading.index : htmlContent.length;

      // Add the content including this section
      segments.push({
        html: htmlContent.slice(lastIndex, sectionEnd),
        soundId: inlineSounds[matchingKey]
      });

      lastIndex = sectionEnd;
    }
  }

  // Add remaining content
  if (lastIndex < htmlContent.length) {
    segments.push({
      html: htmlContent.slice(lastIndex)
    });
  }

  // If no segments were created, return the full content
  if (segments.length === 0) {
    return [{ html: htmlContent }];
  }

  return segments;
}

/**
 * Client-side wrapper for blog article content.
 * Injects sound players inline after matching H3 headings.
 */
export const BlogArticleContent: React.FC<BlogArticleContentProps> = ({
  htmlContent,
  soundCategory,
  soundLimit = 10,
  inlineSounds,
  referenceSoundIds,
  soundCountLabel = PUBLIC_SOUND_COUNT_LABEL,
}) => {
  // Parse content into segments with sound IDs
  const segments = useMemo(() => {
    if (!inlineSounds || Object.keys(inlineSounds).length === 0) {
      return [{ html: htmlContent }];
    }
    return parseContentWithSounds(htmlContent, inlineSounds);
  }, [htmlContent, inlineSounds]);

  const inlineSoundIds = useMemo(() => {
    return [...new Set(segments.flatMap(s => s.soundId ? [s.soundId] : []))];
  }, [segments]);

  // A reference already embedded under its heading is not listed again.
  const listedSoundIds = useMemo(() => {
    const inline = new Set(inlineSoundIds);
    return [...new Set(referenceSoundIds ?? [])].filter(id => !inline.has(id));
  }, [inlineSoundIds, referenceSoundIds]);

  const soundIds = useMemo(
    () => [...inlineSoundIds, ...listedSoundIds],
    [inlineSoundIds, listedSoundIds]
  );

  // Fetch inline and listed sounds together (server-resolved audioUrl).
  const { data: soundsById, isLoading: isLoadingById } = useSWR<Record<string, Sound>>(
    soundIds.length > 0 ? `blog-sounds-by-ids-${soundIds.join(',')}` : null,
    async () => {
      try {
        const batches: string[][] = [];
        for (let i = 0; i < soundIds.length; i += MAX_IDS_PER_REQUEST) {
          batches.push(soundIds.slice(i, i + MAX_IDS_PER_REQUEST));
        }
        const pages = await Promise.all(batches.map(async (ids) => {
          const params = new URLSearchParams({ ids: ids.join(',') });
          const res = await fetch(`/api/sounds?${params.toString()}`);
          if (!res.ok) {
            Sentry.captureException(
              new Error(`Failed to load blog sounds (${res.status})`),
              { tags: { context: 'BlogArticleContent' } }
            );
            return [];
          }
          const data = (await res.json()) as { sounds?: Sound[] };
          return data.sounds ?? [];
        }));
        return Object.fromEntries(pages.flat().map((sound) => [sound.id, sound]));
      } catch (err) {
        Sentry.captureException(err, { tags: { context: 'BlogArticleContent' } });
        return {};
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const listedSounds = soundsById
    ? listedSoundIds.flatMap(id => soundsById[id] ? [soundsById[id]] : [])
    : [];
  const showCategorySounds = soundCategory !== undefined && !inlineSounds && listedSoundIds.length === 0;

  // Fetch sounds by category via GET /api/sounds (legacy behavior, downloads desc)
  const { data: categorySounds, isLoading: isLoadingCategory } = useSWR<Sound[]>(
    showCategorySounds ? `blog-sounds-category-${soundCategory}-${soundLimit}` : null,
    async () => {
      const params = new URLSearchParams({
        per_page: String(soundLimit),
        sort: 'downloads',
        order: 'desc',
      });
      if (soundCategory) params.set('category', soundCategory);
      const res = await fetch(`/api/sounds?${params.toString()}`);
      if (!res.ok) return [];
      const data = (await res.json()) as { sounds?: Sound[] };
      return data.sounds ?? [];
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return (
    <>
      {/* Render content segments with inline sound embeds */}
      {segments.map((segment) => (
        <React.Fragment key={getSegmentKey(segment)}>
          <div
            className="blog-prose max-w-[70ch] article-content"
            // eslint-disable-next-line react-doctor/no-danger
            dangerouslySetInnerHTML={{ __html: segment.html }}
          />
          {segment.soundId && (
            <div className="my-4">
              {isLoadingById || !soundsById ? (
                <BlogSoundEmbedSkeleton />
              ) : soundsById[segment.soundId] ? (
                <BlogSoundEmbed sound={soundsById[segment.soundId]} />
              ) : null}
            </div>
          )}
        </React.Fragment>
      ))}

      {listedSoundIds.length > 0 && (isLoadingById || listedSounds.length > 0) && (
        <div className="mt-12 pt-8 border-t border-[var(--tls-border-subtle)]">
          <h3 className="text-lg font-semibold text-[var(--tls-fg-strong)] mb-6">
            Listen to These Sounds
          </h3>
          <div className="space-y-3">
            {isLoadingById || !soundsById
              ? BLOG_SOUND_SKELETON_ROWS.map((row) => <BlogSoundEmbedSkeleton key={row} />)
              : listedSounds.map((sound) => <BlogSoundEmbed key={sound.id} sound={sound} />)}
          </div>
        </div>
      )}

      {/* Legacy: Sound embeds section at bottom (for category-based fetching) */}
      {showCategorySounds && (
        <div className="mt-12 pt-8 border-t border-[var(--tls-border-subtle)]">
          <div className="flex items-center gap-2 mb-6">
            <div className="size-2 rounded-[var(--radius-pill)] bg-[var(--tls-brand)]" />
            <h3 className="text-lg font-semibold text-[var(--tls-fg-strong)]">
              Most downloaded
            </h3>
          </div>

          {isLoadingCategory ? (
            <div className="space-y-3">
              {BLOG_SOUND_SKELETON_ROWS.map((row) => (
                <BlogSoundEmbedSkeleton key={row} />
              ))}
            </div>
          ) : categorySounds && categorySounds.length > 0 ? (
            <div className="space-y-3">
              {categorySounds.map((sound) => (
                <BlogSoundEmbed key={sound.id} sound={sound} />
              ))}
              <div className="pt-4 text-center">
                <Link
                  href="/sounds"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--tls-brand)] text-[var(--tls-brand-fg)] font-medium text-sm rounded-[var(--radius-pill)] hover:bg-[var(--tls-brand-hover)] transition-colors"
                >
                  Browse {soundCountLabel} More Sounds →
                </Link>
              </div>
            </div>
          ) : (
            <p className="text-[var(--tls-fg-subtle)] text-sm">
              No sounds available in this category yet.
            </p>
          )}
        </div>
      )}
    </>
  );
};
